import type { BookVariantCommandV1, BookVariantStateV1 } from "@lifescapsule/contracts";
import { sendReplayableVariantCommand } from "../book-variants/variant-api";
import { studioCommandDigest } from "./studio-command-replay";

/** Never clear a paid command's retry identity on a generic/latest-state acknowledgement. */
export async function sendNativeStudioCommand(
  bookId: string,
  idToken: string,
  command: BookVariantCommandV1,
) {
  const designDigest = ["SET_SETTINGS", "SET_COVER", "SET_INSCRIPTION"].includes(command.action)
    ? await studioCommandDigest(command)
    : null;
  return sendReplayableVariantCommand(
    bookId,
    idToken,
    command,
    (state) => {
      validateNativeStudioReply(bookId, command, state);
      if (designDigest && state.designReceipt?.commandDigest !== designDigest)
        throw Error("The reply did not confirm this exact design choice.");
      return state;
    },
    true,
  );
}
export function validateNativeStudioReply(
  bookId: string,
  command: BookVariantCommandV1,
  state: BookVariantStateV1,
) {
  if (state.plan && state.plan.bookId !== bookId) throw Error("The reply belongs to another book.");
  if (
    command.action === "RENDER_PROOF" &&
    (!state.proofReceipt || state.proofReceipt.planDigest !== command.planDigest)
  )
    throw Error("The reply did not confirm this exact preview request.");
  if (command.action === "APPROVE_EDITION") {
    const edition = state.acceptedEdition ?? state.edition;
    if (!edition?.editionId || edition.bookId !== bookId || edition.proofId !== command.proofId)
      throw Error("The reply did not confirm this exact edition.");
  }
  if (command.action === "COMPILE" && (!state.plan || !state.planCurrent))
    throw Error("The saved words have not been laid out yet.");
  if (command.action === "PAINT" || command.action === "REPAINT") {
    const receipt = state.paintReceipt;
    const requested = command.action === "PAINT" ? command.slotIds : [command.slotId];
    if (
      !receipt ||
      !receipt.jobId ||
      receipt.planDigest !== command.planDigest ||
      !receipt.slotIds.length ||
      new Set(receipt.slotIds).size !== receipt.slotIds.length ||
      receipt.slotIds.some((id) => !requested.includes(id))
    )
      throw Error("The reply did not confirm this picture request. Retry the same request.");
  }
  if (
    command.action === "APPROVE_LOOK" &&
    (!state.lookApproved || state.plan?.planDigest !== command.planDigest)
  )
    throw Error("The reply did not confirm this look.");
  if (
    command.action === "APPROVE_RECIPE" &&
    !state.recipeApprovals?.some(
      (entry) => entry.recipeId === command.recipeId && entry.revision === command.revision,
    )
  )
    throw Error("The reply did not confirm this recipe version.");
}
